import { FUNDING_MODEL_NAME } from "@giftforward/naming";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { POSTPAID_BILLING_CONTACT_NOTE, POSTPAID_PO_NOTE } from "@/constants/review";
import { useBillingAddresses, useCreateBillingAddress } from "@/hooks/useBillingAddresses";
import { useCurrentTerms } from "@/hooks/useLegalDocuments";
import type { PaymentMethod } from "@/interfaces/paymentMethod";
import type { PostpaidDepositMode, PostpaidLaunchInput, PostpaidSplit } from "@/interfaces/review";
import { formatMoney } from "@/lib/campaigns";
import {
  addDaysIso,
  postpaidAcceptanceLabel,
  postpaidPaymentMethodLabel,
  termsDate,
} from "@/lib/checkout";
import { humanErrorMessage } from "@/lib/entitlementError";
import { cardBrandLabel } from "@/utils/payment-helper";

export interface PostpaidConfirmProps {
  split: PostpaidSplit;
  minUpfrontPc: number;
  perGift: number;
  /** When the campaign closes — the final invoice is raised then. */
  closesOn: string;
  netTermsDays: number;
  /** Days after close the final invoice is issued — the organisation's, not chosen here. */
  finalInvoiceLagDays: number;
  /** How the invoices are to be paid — the organisation's, not chosen here. */
  finalPaymentMethod: number;
  /** The funding wallet — billing contacts belong to it. */
  fundingGroupId: number | null;
  /** The wallet's name, shown under the billing contact. */
  fundingGroupName?: string | null;
  card: PaymentMethod | null;
  processing: boolean;
  errorMessage?: string | null;
  onCancel: () => void;
  onConfirm: (postpaid: PostpaidLaunchInput) => void;
}

/** "Exp 03 / 28", or null when Stripe has not reported an expiry. */
function expiryLabel(card: PaymentMethod): string | null {
  if (card.expMonth === null || card.expYear === null) return null;
  return `Exp ${String(card.expMonth).padStart(2, "0")} / ${String(card.expYear).slice(-2)}`;
}

/**
 * The postpaid body of "Confirm & pay" (mockups 6 and 7): what is due today → how to pay it, card
 * or invoice (only when something is owed) → the card, or the invoice note → the postpaid terms →
 * billing contact and PO → the acceptance → pay.
 *
 * Due today is the deposit less any existing credits put toward it. The acceptance is RECORDED —
 * user, time, terms version and the label exactly as shown — so the label sent is built from the
 * same figures that are on screen.
 */
export default function PostpaidConfirm({
  split,
  minUpfrontPc,
  perGift,
  closesOn,
  netTermsDays,
  finalInvoiceLagDays,
  finalPaymentMethod,
  fundingGroupId,
  fundingGroupName,
  card,
  processing,
  errorMessage,
  onCancel,
  onConfirm,
}: PostpaidConfirmProps) {
  const owed = split.dueToday > 0;
  const [mode, setMode] = useState<"card" | "invoice">("card");
  const { data: addresses } = useBillingAddresses(fundingGroupId);
  const { data: terms } = useCurrentTerms();
  const active = useMemo(() => (addresses ?? []).filter((a) => a.isActive), [addresses]);
  const [addressId, setAddressId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [poNumber, setPoNumber] = useState("");
  const [accepted, setAccepted] = useState(false);

  // The wallet moved, so its contacts did: keep the pick if it is still this wallet's, else take
  // the wallet's default — never another wallet's contact.
  useEffect(() => {
    setAddressId((current) =>
      current !== null && active.some((a) => a.id === current)
        ? current
        : ((active.find((a) => a.isDefault) ?? active[0])?.id ?? null),
    );
  }, [active]);

  const depositDue = termsDate(addDaysIso(new Date().toISOString(), netTermsDays));
  // Issued `lag` days after close — the audit window — and due `net` days after that.
  const finalIssuedIso = addDaysIso(closesOn, finalInvoiceLagDays);
  const finalIssued = termsDate(finalIssuedIso);
  const finalDue = termsDate(addDaysIso(finalIssuedIso, netTermsDays));
  const payBy = postpaidPaymentMethodLabel(finalPaymentMethod);
  const label = postpaidAcceptanceLabel({
    deposit: formatMoney(split.deposit),
    finalIssued,
    finalDue,
    payBy,
  });
  const gifts = perGift > 0 ? Math.round(split.deposit / perGift) : null;
  // The client chose to pay more than the minimum at Step 4 — the terms name the deposit they are
  // actually paying, with the minimum beneath it, so the acceptance records their figure.
  const raised = split.deposit > split.minDeposit;
  const depositPc =
    raised && split.budget > 0 ? Math.round((split.deposit / split.budget) * 100) : minUpfrontPc;
  const selected = active.find((a) => a.id === addressId) ?? null;

  // Nothing owed today (existing credits cover the deposit) → the wallet pays it.
  const depositMode: PostpaidDepositMode = owed ? mode : "credits";
  const needsCard = depositMode === "card";
  const canConfirm = accepted && addressId !== null && (!needsCard || card !== null) && !processing;
  const cta =
    depositMode === "credits"
      ? "Launch campaign"
      : depositMode === "invoice"
        ? "Send invoice & launch"
        : `Pay ${formatMoney(split.dueToday)}`;

  return (
    <>
      <div className="flex items-baseline justify-between border-smoke border-t pt-3.5 font-lato text-[15px] text-graphite">
        <span>
          {raised
            ? `Due today (${depositPc}% deposit)`
            : `Due today (${minUpfrontPc}% minimum deposit)`}
        </span>
        <span className="font-crimson font-normal text-[26px] text-rodeo-black">
          {formatMoney(split.dueToday)}
        </span>
      </div>
      {split.creditsApplied > 0 && (
        <p className="mt-1 mb-0 font-lato text-[12px] text-graphite">
          {formatMoney(split.deposit)} {raised ? "deposit" : "minimum deposit"}, less{" "}
          {formatMoney(split.creditsApplied)} of your existing credits.
        </p>
      )}

      {owed && (
        <div className="mt-4">
          <div className="mb-2 font-lato text-[13px] font-semibold text-rodeo-black">
            How would you like to pay the deposit?
          </div>
          <fieldset className="m-0 grid grid-cols-2 gap-1 rounded-card border-0 bg-light-dust p-1">
            <legend className="sr-only">How would you like to pay the deposit?</legend>
            {(
              [
                { key: "card", label: "Card on file" },
                { key: "invoice", label: "Send me an invoice" },
              ] as const
            ).map((m) => (
              <label
                key={m.key}
                className={`cursor-pointer rounded-btn px-3 py-2.5 text-center font-lato text-[12.5px] has-focus-visible:outline-2 ${
                  mode === m.key
                    ? "bg-white font-bold text-rodeo-black shadow-[0_1px_2px_rgba(0,0,0,.08)]"
                    : "text-graphite"
                }`}
              >
                <input
                  type="radio"
                  name="postpaid-deposit-mode"
                  className="sr-only"
                  checked={mode === m.key}
                  onChange={() => setMode(m.key)}
                />
                {m.label}
              </label>
            ))}
          </fieldset>
        </div>
      )}

      {owed &&
        mode === "card" &&
        (card ? (
          <div className="my-4.5 flex items-center gap-2.5 rounded-lg border border-smoke px-3.5 py-3">
            <span className="rounded-sm bg-rodeo-black px-1.75 py-0.75 font-extrabold font-lato text-[10px] text-white uppercase tracking-[0.5px]">
              {cardBrandLabel(card.brand)}
            </span>
            <span className="font-lato font-semibold text-rodeo-black text-sm">
              •••• {card.last4 ?? "••••"}
            </span>
            {expiryLabel(card) && (
              <span className="ml-auto font-lato text-graphite text-xs">{expiryLabel(card)}</span>
            )}
          </div>
        ) : (
          <p className="my-4.5 font-lato text-[13px] text-good-wine">
            Add a payment card to this wallet to pay the deposit by card.
          </p>
        ))}
      {owed && mode === "invoice" && (
        <div className="my-4.5 flex items-start gap-2 rounded-lg bg-light-dust px-3.5 py-3 font-lato text-graphite text-xs leading-[1.5]">
          <ReceiptLongOutlinedIcon
            aria-hidden="true"
            className="mt-px shrink-0 text-dark-dust"
            sx={{ fontSize: 16 }}
          />
          <span>
            We'll email the deposit invoice to your billing contact.{" "}
            <strong className="font-bold text-rodeo-black">Credits are issued up front</strong> so
            your campaign can go live before it is paid — which means the whole campaign budget
            counts against your credit limit until it clears.
          </span>
        </div>
      )}

      <div className="mt-5 rounded-card border border-card-border bg-clear-dust p-4">
        <div className="mb-1 font-lato text-[13px] font-bold text-rodeo-black">
          {FUNDING_MODEL_NAME.postpaid} terms
        </div>
        <TermsLine
          label={raised ? "Deposit" : "Minimum deposit"}
          detail={`${depositPc}% of the ${formatMoney(split.budget)} campaign budget${
            gifts ? ` — ${gifts} whole gifts` : ""
          }${raised ? `. Minimum ${minUpfrontPc}% (${formatMoney(split.minDeposit)})` : ""}`}
          value={formatMoney(split.deposit)}
        />
        {owed && mode === "invoice" && (
          <TermsLine
            label="Initial invoice (minimum deposit)"
            value={formatMoney(split.dueToday)}
            sub={`due ${depositDue}`}
          />
        )}
        <TermsLine
          label="Final invoice (post campaign)"
          detail={`Up to ${formatMoney(split.ceiling)}. You are billed only for gifts your recipients actually claim.`}
          value="based on actual redemption"
          sub={`issued ${finalIssued} · due ${finalDue}`}
        />
        <TermsLine label="Pay by" value={payBy} sub={`Net ${netTermsDays}`} />
      </div>

      <div className="mt-4">
        <div className="mb-2 font-lato text-[13px] font-semibold text-rodeo-black">
          Billing contact
        </div>
        {/* Drawn as the mockup's contact box; a native select sits over it, so keyboard and screen
            reader users get a real control. */}
        <div className="relative flex items-center gap-2.5 rounded-btn border border-smoke bg-white px-3.5 py-2.5 has-focus-visible:outline-2">
          <BusinessOutlinedIcon
            className="text-graphite"
            sx={{ fontSize: 18 }}
            aria-hidden="true"
          />
          <span className="flex-1 font-lato text-[13px] text-rodeo-black">
            {selected
              ? [selected.label ?? selected.contactName, selected.email].filter(Boolean).join(" · ")
              : "No billing contact for this wallet yet"}
            {fundingGroupName && (
              <span className="mt-0.5 block text-[11.5px] text-graphite">{fundingGroupName}</span>
            )}
          </span>
          <ExpandMoreIcon className="text-graphite" sx={{ fontSize: 20 }} aria-hidden="true" />
          <select
            aria-label="Billing contact"
            className="absolute inset-0 cursor-pointer opacity-0"
            value={addressId ?? ""}
            disabled={active.length === 0}
            onChange={(e) => setAddressId(e.target.value ? Number(e.target.value) : null)}
          >
            {active.map((a) => (
              <option key={a.id} value={a.id}>
                {[a.label ?? a.contactName, a.email].filter(Boolean).join(" · ")}
              </option>
            ))}
          </select>
        </div>
        {adding && fundingGroupId !== null ? (
          <AddBillingContact
            groupId={fundingGroupId}
            onDone={(id) => {
              setAdding(false);
              if (id !== null) setAddressId(id);
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            disabled={fundingGroupId === null}
            className="mt-2 cursor-pointer border-0 bg-transparent p-0 font-lato text-[11px] font-bold uppercase tracking-[1.5px] text-dust-ink"
          >
            + Add billing contact
          </button>
        )}

        <label
          htmlFor="postpaid-po"
          className="mt-4 mb-1 block font-lato text-[13px] font-semibold text-rodeo-black"
        >
          PO number <span className="font-normal text-graphite">(optional)</span>
        </label>
        <input
          id="postpaid-po"
          type="text"
          maxLength={100}
          value={poNumber}
          onChange={(e) => setPoNumber(e.target.value)}
          placeholder="e.g. PO-4471-NW"
          className="w-full rounded-btn border border-smoke bg-white px-3.5 py-2.5 font-lato text-[13px] text-rodeo-black placeholder:text-graphite"
        />
        <p className="mt-1.5 mb-0 font-lato text-[11.5px] leading-[1.45] text-graphite">
          {POSTPAID_PO_NOTE}
        </p>
        <p className="mt-2 mb-0 font-lato text-[11.5px] leading-[1.45] text-graphite">
          {POSTPAID_BILLING_CONTACT_NOTE}
        </p>
      </div>

      <label className="mt-5 flex cursor-pointer items-start gap-2.5 font-lato text-[13px] leading-normal text-rodeo-black">
        <input
          type="checkbox"
          className="gfg-checkbox mt-px h-[18px] w-[18px] shrink-0 cursor-pointer"
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
        />
        {/* The same sentence as `label` — which is what gets recorded — with its figures emphasised. */}
        <span className="flex-1">
          I accept the payment terms for this campaign — a{" "}
          <b className="font-bold">{formatMoney(split.deposit)}</b> deposit today, and an invoice
          after close for the gifts claimed, due <b className="font-bold">{finalDue}</b>, under the{" "}
          {terms?.url ? (
            <a
              className="text-dust-ink underline"
              href={terms.url}
              target="_blank"
              rel="noreferrer"
            >
              Gifts for Good Terms and Conditions
            </a>
          ) : (
            "Gifts for Good Terms and Conditions"
          )}
          .
        </span>
      </label>

      {errorMessage && (
        <p
          role="alert"
          className="mt-3 mb-0 flex items-center gap-1.5 font-lato font-semibold text-[13px] text-good-wine leading-snug"
        >
          <ErrorOutlineIcon className="shrink-0" sx={{ fontSize: 17 }} aria-hidden="true" />
          {errorMessage}
        </p>
      )}

      <button
        type="button"
        disabled={!canConfirm}
        aria-busy={processing}
        onClick={() =>
          addressId !== null &&
          onConfirm({
            depositMode,
            billingAddressId: addressId,
            poNumber: poNumber.trim() || null,
            termsAccepted: true,
            termsLabel: label,
          })
        }
        className="mt-5 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-rodeo-black py-3.5 font-bold font-lato text-sm text-white tracking-[0.5px] hover:bg-[#222] disabled:cursor-default disabled:opacity-60"
      >
        {processing ? (
          <>
            <span
              aria-hidden="true"
              className="h-4 w-4 animate-spin rounded-full border-2 border-white/35 border-t-white"
            />
            Processing…
          </>
        ) : (
          <>
            <LockIcon aria-hidden="true" sx={{ fontSize: 16 }} />
            {cta}
          </>
        )}
      </button>
      <button
        type="button"
        disabled={processing}
        onClick={onCancel}
        className="mt-2.5 w-full cursor-pointer py-1.5 font-bold font-lato text-graphite text-xs uppercase tracking-[1px] hover:text-rodeo-black disabled:cursor-default disabled:opacity-60"
      >
        Cancel
      </button>
    </>
  );
}

function TermsLine({
  label,
  detail,
  value,
  sub,
}: {
  label: string;
  detail?: string;
  value: ReactNode;
  sub?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-6 border-card-border border-b py-2.5 last:border-b-0">
      <span className="font-lato text-[13px] text-graphite">
        {label}
        {detail && <span className="mt-0.5 block text-[11.5px] text-graphite/80">{detail}</span>}
      </span>
      <span className="shrink-0 text-right font-lato text-[13px] font-semibold text-rodeo-black">
        {value}
        {sub && (
          <>
            <br />
            <span className="font-normal text-graphite">{sub}</span>
          </>
        )}
      </span>
    </div>
  );
}

/** "+ Add billing contact" — a contact on the funding wallet, selected once it is created. */
function AddBillingContact({
  groupId,
  onDone,
}: {
  groupId: number;
  onDone: (createdId: number | null) => void;
}) {
  const create = useCreateBillingAddress(groupId);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const valid = name.trim() !== "" && /\S+@\S+\.\S+/.test(email.trim());
  const field =
    "w-full rounded-btn border border-smoke bg-white px-3 py-2 font-lato text-[13px] text-rodeo-black";
  return (
    <div className="mt-2 rounded-card border border-card-border bg-clear-dust p-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <input
          aria-label="Contact name"
          placeholder="Contact name, e.g. Accounts Payable"
          className={field}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          aria-label="Email"
          type="email"
          placeholder="Email"
          className={field}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      {error && <p className="mt-2 mb-0 font-lato text-[12px] text-good-wine">{error}</p>}
      <div className="mt-2 flex gap-3">
        <button
          type="button"
          disabled={!valid || create.isPending}
          onClick={() =>
            create.mutate(
              { label: name.trim(), contactName: name.trim(), email: email.trim() },
              {
                onSuccess: (a) => onDone(a.id),
                onError: (err) => setError(humanErrorMessage(err, "Couldn't add the contact.")),
              },
            )
          }
          className="cursor-pointer border-0 bg-transparent p-0 font-lato text-[11px] font-bold uppercase tracking-[1.5px] text-dust-ink disabled:opacity-50"
        >
          {create.isPending ? "Adding…" : "Add contact"}
        </button>
        <button
          type="button"
          onClick={() => onDone(null)}
          className="cursor-pointer border-0 bg-transparent p-0 font-lato text-[11px] font-bold uppercase tracking-[1.5px] text-graphite"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
